import SamIcon from '@mui/icons-material/AutoAwesome';
import { Paper, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { multiplyBorderRadius } from '@shared/theme/util';
import { formatDateAndTime } from '@shared/utils/date';
import { useMemo } from 'react';
import { Markdown } from '@/component/Markdown';
import { isMarkdown } from '@/component/Markdown/utils';
import { ChatMessage } from '@/feature/chatbot/types';
import { useTexts } from '@/feature/lang';
import Translate from '../../Translate';
import { MessageMenu } from './MessageMenu';

const MessageBubbleContainer = styled(Paper, {
  shouldForwardProp: (prop) =>
    !['isUser', 'isStreaming'].includes(prop as string),
})<{ isUser: boolean; isStreaming: boolean }>(
  ({ theme, isUser, isStreaming }) => ({
    maxWidth: '80%',
    width: 'fit-content',
    minWidth: isStreaming ? '80%' : 0,
    marginLeft: isUser ? 'auto' : theme.spacing(2),
    marginRight: isUser ? theme.spacing(2) : 'auto',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    paddingBottom: theme.spacing(0.5),
    backgroundColor: isUser ? theme.palette.getGrey(20) : 'unset',
    border: isUser ? 'none' : `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
    borderRadius: multiplyBorderRadius(theme, 1.5),
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    position: 'relative',
    '&:last-child': {
      marginBottom: 0,
    },
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      left: isUser ? 'auto' : -11,
      right: isUser ? -11 : 'auto',
      bottom: theme.spacing(0),
      width: 7,
      height: 7,
      borderRadius: '50%',
      backgroundColor: isUser ? theme.palette.getGrey(20) : 'unset',
      border: isUser ? 'none' : `1px solid ${theme.palette.divider}`,
    },
  })
);

const MessageHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
}));

const HeaderText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.75rem',
  fontWeight: 500,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const Timestamp = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.75rem',
  flexGrow: 1,
  marginRight: theme.spacing(1),
}));

const MessageContent = styled('div')(({ theme }) => ({
  '& > p, & > div, & > span': {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    margin: 0,
    marginBottom: theme.spacing(1),
  },
  '& > p:last-child, & > div:last-child, & > span:last-child': {
    marginBottom: 0,
  },
}));

const PlainTextContent = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  lineHeight: 1.5,
  margin: 0,
  marginBottom: theme.spacing(1),
}));

const BlinkingCursor = styled('span')(({ theme }) => ({
  display: 'inline-block',
  width: '1px',
  height: '1em',
  backgroundColor: theme.palette.text.primary,
  marginLeft: '2px',
  animation: 'blink 1s step-end infinite',
  '@keyframes blink': {
    '0%': { opacity: 0.5 },
    '50%': { opacity: 0 },
  },
}));

interface MessageBubbleProps {
  message: ChatMessage;
}

export const MessageBubble = ({ message }: MessageBubbleProps) => {
  const texts = useTexts().component.chatbot.messageBubble;
  const isUser = message.sender === 'user';
  const content = message.content ?? '';
  const isStreaming = message.isStreaming ?? false;

  const shouldRenderAsMarkdown = useMemo(() => isMarkdown(content), [content]);

  return (
    <MessageBubbleContainer
      isUser={isUser}
      isStreaming={isStreaming}
      elevation={0}
    >
      <MessageHeader>
        <HeaderText>
          {isUser ? (
            <Translate text={texts.userTitle} />
          ) : (
            <>
              <SamIcon sx={{ fontSize: 16 }} />
              <Translate text={texts.chatbotTitle} />
            </>
          )}
        </HeaderText>
        <Timestamp>{formatDateAndTime(message.timestamp)}</Timestamp>
        <MessageMenu message={message} />
      </MessageHeader>
      <MessageContent>
        {shouldRenderAsMarkdown ? (
          <Markdown>{content}</Markdown>
        ) : (
          <PlainTextContent variant="body2">{content}</PlainTextContent>
        )}
        {message.isStreaming && <BlinkingCursor />}
      </MessageContent>
    </MessageBubbleContainer>
  );
};
